/**
 * The planting palette.
 *
 * Content, not engine: a new project replaces this file. **Species ids are
 * stored in saves, in share links and in the register — never rename or
 * reuse one.** Adding a species also means appending it to `WIRE_SPECIES`
 * below, and checking `src/models/plants.ts`, which tunes a few silhouettes
 * by id.
 *
 * Six drought-hardy plants: five trees of the arid subtropics and one
 * succulent ornamental. Each is real, and each blurb says only what is true
 * of the plant itself. They used to be described through one particular
 * park's planting programme, and that framing went with the venue.
 *
 * **This is not yet a world mix.** Broadening the palette so a garden grown
 * in Lagos or Lima holds plants that belong there is the next piece of work;
 * see the rebrand spec in `docs/superpowers/specs/`.
 *
 * `drawShare` is a rarity weight, not a claim about anywhere. The five field
 * species' weights sum to 100 so a pack's odds read straight off them; the
 * succulent is drawn separately (`PACK_DRAW`).
 *
 * Colours are authored to read well against both bright outdoor camera feeds
 * and dim indoor ones, so they are deliberately a little more saturated than
 * life.
 */

export type SpeciesId =
  | 'acacia'
  | 'ghaf'
  | 'date-palm'
  | 'sidr'
  | 'tamarix'
  | 'desert-rose';

/** Which procedural builder draws this species. */
export type PlantForm = 'palm' | 'succulent' | 'canopy' | 'shrub';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export interface Species {
  id: SpeciesId;
  /** Display name, English. */
  name: string;
  /** Display name, Arabic — shown alongside on the collection card. */
  nameAr: string;
  /** Botanical name, italicised in the UI. */
  latin: string;
  form: PlantForm;
  rarity: Rarity;
  /**
   * Rarity weight as a percentage, driving the seed draw. The five field
   * species sum to 100. Null for the ornamental, which is never in the field
   * draw at all and so has no share of it.
   */
  drawShare: number | null;
  /** Foliage colour, hex. */
  leaf: number;
  /** Trunk / stem colour, hex. */
  stem: number;
  /** Bloom colour used for flowers and the burst particles at full growth. */
  bloom: number;
  /**
   * Metres tall at the final growth stage. Drives the whole silhouette.
   *
   * These are diorama scale, not life scale. The plot holds twelve beds
   * roughly 0.3 m apart, so a mature specimen has to keep its canopy inside
   * about that much room — at life-like proportions one date palm's crown
   * covered the entire garden and twelve of them rendered as a single
   * undifferentiated mass.
   */
  matureHeight: number;
  /**
   * Root note of the scale this plant sings, as a semitone offset from C4.
   * Each species gets its own tonal centre so a full garden harmonises
   * instead of clashing.
   */
  rootSemitone: number;
  /**
   * Wall-clock seconds from sowing to maturity, unattended.
   *
   * The ladder is deliberately steep and deliberately front-loaded. Somebody
   * who opens the link may only ever open it once, so the commonest species
   * has to pay off inside a minute — long enough to feel
   * like growth, short enough that the first fact card lands before they walk
   * away. Everything above that is the reason to come back: the desert rose
   * is a tomorrow reward, and it is meant to be.
   *
   * Tending accelerates but never replaces this; see `Plant.tend()`.
   */
  growSeconds: number;
  /** Shown when the species is first grown to maturity. */
  blurb: string;
}

/** Points a matured specimen contributes to a gardener's register score. */
export const RARITY_POINTS: Record<Rarity, number> = {
  common: 10,
  uncommon: 20,
  rare: 35,
  legendary: 80,
};

export const SPECIES: Record<SpeciesId, Species> = {
  acacia: {
    id: 'acacia',
    name: 'Umbrella Thorn',
    nameAr: 'سَمُر',
    latin: 'Vachellia tortilis',
    form: 'canopy',
    rarity: 'common',
    drawShare: 35,
    leaf: 0x5c8746,
    stem: 0x6d5a44,
    bloom: 0xf0e4a8,
    matureHeight: 0.38,
    rootSemitone: 9, // A
    growSeconds: 45,
    blurb:
      'Its flat crown holds shade level over the ground like a parasol — ' +
      'which is why so much shelters beneath it and so little grows there.',
  },
  ghaf: {
    id: 'ghaf',
    name: 'Ghaf',
    nameAr: 'غاف',
    latin: 'Prosopis cineraria',
    form: 'canopy',
    rarity: 'common',
    drawShare: 22,
    leaf: 0x6b9455,
    stem: 0x7a6248,
    bloom: 0xe3d178,
    matureHeight: 0.4,
    rootSemitone: 5, // F
    growSeconds: 150,
    blurb:
      'Roots reach thirty metres down for water. It holds its leaves through ' +
      'drought and enriches the soil it stands in.',
  },
  'date-palm': {
    id: 'date-palm',
    name: 'Date Palm',
    nameAr: 'نخلة',
    latin: 'Phoenix dactylifera',
    form: 'palm',
    rarity: 'uncommon',
    drawShare: 20,
    leaf: 0x4f8a3d,
    stem: 0x8a6b45,
    bloom: 0xd9932f,
    matureHeight: 0.48,
    rootSemitone: 0, // C
    growSeconds: 420,
    blurb:
      'Cultivated for thousands of years. Planted in an avenue the crowns ' +
      'meet overhead, and the path beneath stays shaded at midday.',
  },
  sidr: {
    id: 'sidr',
    name: 'Sidr',
    nameAr: 'سِدر',
    latin: 'Ziziphus spina-christi',
    form: 'canopy',
    rarity: 'rare',
    drawShare: 13,
    leaf: 0x4e7d3f,
    stem: 0x6b5742,
    bloom: 0xd7e08a,
    matureHeight: 0.36,
    rootSemitone: 2, // D
    growSeconds: 2_700,
    blurb:
      'The lote tree. Dense, thorny and evergreen, it fruits through drought, ' +
      'and the honey from its flowers is prized wherever it grows.',
  },
  tamarix: {
    id: 'tamarix',
    name: 'Athl Tamarisk',
    nameAr: 'أثل',
    latin: 'Tamarix aphylla',
    form: 'shrub',
    rarity: 'rare',
    drawShare: 10,
    leaf: 0x7d9470,
    stem: 0x8b8071,
    bloom: 0xe7cfe0,
    matureHeight: 0.3,
    rootSemitone: 7, // G
    growSeconds: 900,
    blurb:
      'It sweats salt out through its own leaves to survive brackish ground, ' +
      'and has been planted as a windbreak for centuries.',
  },
  'desert-rose': {
    id: 'desert-rose',
    name: 'Desert Rose',
    nameAr: 'وردة الصحراء',
    latin: 'Adenium obesum',
    form: 'succulent',
    rarity: 'legendary',
    drawShare: null,
    leaf: 0x3f7a4a,
    stem: 0xa8917a,
    bloom: 0xe8557f,
    matureHeight: 0.2,
    rootSemitone: 4, // E
    growSeconds: 10_800,
    blurb:
      'Not a tree at all: a swollen-stemmed succulent that stores its own ' +
      'water, and flowers while everything around it waits for rain.',
  },
};

/** Display order: commonest first, so the rail reads as a rarity ladder. */
export const SPECIES_ORDER: SpeciesId[] = [
  'acacia',
  'ghaf',
  'date-palm',
  'sidr',
  'tamarix',
  'desert-rose',
];

/** Colour used for rarity chips and card borders. */
export const RARITY_COLOR: Record<Rarity, string> = {
  common: '#8fae7d',
  uncommon: '#6ec8d4',
  rare: '#9b8fe0',
  legendary: '#f0a63f',
};

export const RARITY_LABEL: Record<Rarity, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  legendary: 'Legendary',
};

/** The species selected on the rail when the game opens. */
export const DEFAULT_SPECIES: SpeciesId = 'date-palm';

/**
 * Species order for the share-link wire format (`src/game/share.ts`).
 * **Append only, never reorder.**
 *
 * An index here is baked into every link ever shared. Reordering this list
 * silently turns other people's date palms into sidr.
 */
export const WIRE_SPECIES: readonly SpeciesId[] = [
  'acacia',
  'ghaf',
  'date-palm',
  'sidr',
  'tamarix',
  'desert-rose',
];

/**
 * What a seed pack draws from (`src/game/economy.ts`).
 *
 * `field` is the five trees, commonest first. Their `drawShare` values sum to
 * 100, which is why the draw reads them directly rather than carrying a
 * second table that could drift from this one — `tests/economy.test.ts`
 * fails if the two ever disagree.
 *
 * `special` is the ornamental. It has no share of the field draw, so it
 * cannot sit in that table at all: it is drawn first, separately, as its own
 * event.
 */
export const PACK_DRAW: {
  field: readonly SpeciesId[];
  special: { id: SpeciesId; chance: number; toastTitle: string };
} = {
  field: ['acacia', 'ghaf', 'date-palm', 'sidr', 'tamarix'],
  special: { id: 'desert-rose', chance: 0.04, toastTitle: 'A rare pull' },
};

export function speciesAt(index: number): Species {
  return SPECIES[SPECIES_ORDER[index % SPECIES_ORDER.length]];
}
